import { db } from '@/db/client';
import { isAuthenticationRequiredSyncError } from '@/lib/sync/error-utils';
import { ensureSessionIndexEntry } from '@/lib/sync/session-index';
import type { SessionId } from '@/schemas';
import type { UserId } from '@/schemas/ids';
import { enqueueSyncJob, registerSyncPlugin } from '../plugins.js';
import type { SyncPlugin } from '../plugins.js';

export const SESSION_INDEX_SYNC_PLUGIN_KEY = 'session-index';

export interface SessionIndexSyncOptions {
  readonly createdAt?: number;
  readonly updatedAt?: number;
  readonly deletedAt?: number;
}

export interface SessionIndexSyncScope {
  readonly sessionId: SessionId;
  readonly userId: UserId;
  readonly options: SessionIndexSyncOptions;
}

const sessionIndexPlugin: SyncPlugin<SessionIndexSyncScope> = {
  key: SESSION_INDEX_SYNC_PLUGIN_KEY,
  createJobKey(scope: SessionIndexSyncScope) {
    const jobType = scope.options.deletedAt ? 'delete' : 'upsert';
    return `session-index:${jobType}:${String(scope.userId)}:${scope.sessionId}`;
  },
  createMetadata(scope: SessionIndexSyncScope) {
    return {
      entity: 'session-index',
      scope: `${String(scope.userId)}:${scope.sessionId}`,
      source: 'session-index-sync-plugin',
    };
  },
  async run(scope: SessionIndexSyncScope) {
    const { sessionId, userId, options } = scope;
    let createdAt = options.createdAt;
    if (createdAt === undefined) {
      const metadata = await db.sessionMetadata.get(sessionId);
      createdAt = metadata?.createdAt ?? Date.now();
    }

    const updatedAt = options.updatedAt ?? Date.now();

    await ensureSessionIndexEntry({
      sessionId,
      userId,
      createdAt,
      updatedAt,
      deletedAt: options.deletedAt,
    });
  },
  shouldRetry(error) {
    return !isAuthenticationRequiredSyncError(error);
  },
};

const registerSessionIndexPlugin = registerSyncPlugin as (
  plugin: SyncPlugin<SessionIndexSyncScope>,
) => SyncPlugin<SessionIndexSyncScope>;
registerSessionIndexPlugin(sessionIndexPlugin);

export function enqueueSessionIndexSync(scope: SessionIndexSyncScope): void {
  const enqueue = enqueueSyncJob as (key: string, scope: SessionIndexSyncScope) => void;
  enqueue(SESSION_INDEX_SYNC_PLUGIN_KEY, scope);
}
